import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import styles from './PaymentGatewayList.module.css';
import { usePermission } from '../../hooks/usePermissions';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import AddPaymentGateway from './AddPaymentGateway';
import { formatAdminDate } from '../../utils/datetime';



const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const PaymentGatewayList = () => {
    const { hasPermission } = usePermission();

    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGatewayId, setSelectedGatewayId] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    // Sorting
    const [sortKey, setSortKey] = useState('transaction_id');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };


    const fetchTransactions = (page = currentPage) => {
        setLoading(true);
        api.get('/paymentgateway')
            .then((response) => {
                let resultsArray = response?.result || response?.data || response || [];
                if (!Array.isArray(resultsArray)) resultsArray = [];

                // Frontend filtering
                let filtered = [...resultsArray];

                // Search filter
                if (searchTerm) {
                    const lSearch = searchTerm.toLowerCase();
                    filtered = filtered.filter(item => {
                        const hasName = item.user_name && item.user_name.toLowerCase().includes(lSearch);
                        const hasProvider = item.service_provider_tid && item.service_provider_tid.toLowerCase().includes(lSearch);
                        const hasId = item.transaction_id && item.transaction_id.toString().includes(lSearch);
                        return hasName || hasProvider || hasId;
                    });
                }

                // Date range filter (by transaction_date)
                if (fromDate) {
                    const from = new Date(fromDate);
                    filtered = filtered.filter(item => new Date(item.transaction_date) >= from);
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    filtered = filtered.filter(item => new Date(item.transaction_date) <= to);
                }

                // Sorting
                filtered.sort((a, b) => {
                    let aVal = a[sortKey];
                    let bVal = b[sortKey];
                    if (aVal === null || aVal === undefined) aVal = '';
                    if (bVal === null || bVal === undefined) bVal = '';
                    const aNum = Number(aVal);
                    const bNum = Number(bVal);
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
                    }
                    return sortOrder === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });

                // Frontend pagination
                const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage) || 1;
                let safePage = page;
                if (safePage > totalFilteredPages) {
                    safePage = totalFilteredPages;
                    setCurrentPage(safePage);
                }
                const startIndex = (safePage - 1) * itemsPerPage;
                const sliced = filtered.slice(startIndex, startIndex + itemsPerPage);

                setTransactions(sliced);
                setTotalPages(totalFilteredPages);
            })
            .catch((error) => {
                console.error('Error fetching payment gateway logs:', error);
                const errMsg = error.response?.data?.message || error.message || 'Failed to fetch';
                toast.error(`Fetch Error: ${errMsg}`);
                setTransactions([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTransactions(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortKey, sortOrder]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchTransactions(1);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) return;
        api.delete(`/paymentgateway/${id}`)
            .then(() => {
                toast.success('Transaction deleted successfully');
                fetchTransactions(currentPage);
            })
            .catch((error) => {
                const errMsg = error.response?.data?.message || error.message || 'Failed to delete';
                toast.error(`Delete Error: ${errMsg}`);
            });
    };

    const getStatusClass = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'captured' || s === 'authorized' || s === 'success') return styles.statusSuccess;
        if (s === 'failed' || s === 'error') return styles.statusFailed;
        return styles.statusPending;
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Payment Gateway Logs</h1>
                    <p className={styles.pageSubtitle}>Track and manage all gateway transactions</p>
                </div>
            </div>

            <div className={styles.card}>
                {/* Filters Bar */}
                <div className={styles.filtersBar}>
                    <div className={styles.dateFilters}>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>From</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={fromDate}
                                onChange={e => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>To</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={toDate}
                                onChange={e => setToDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={styles.searchGroup}>
                        <label className={styles.dateLabel}>Search</label>
                        <div className={styles.searchInputRow}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by ID, User, TX..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleSearch()}
                            />
                            <button className={styles.searchBtn} onClick={handleSearch}>
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            <span>Loading transactions...</span>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('transaction_id')} className={styles.sortable}>TX ID {sortKey === 'transaction_id' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('user_name')} className={styles.sortable}>User Name {sortKey === 'user_name' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('service_provider')} className={styles.sortable}>Service Provider {sortKey === 'service_provider' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th>RP TX ID</th>
                                    <th onClick={() => handleSort('amount')} className={styles.sortable}>Amount {sortKey === 'amount' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('net_amount')} className={styles.sortable}>Net Amount {sortKey === 'net_amount' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('transaction_date')} className={styles.sortable}>Date {sortKey === 'transaction_date' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('transaction_status')} className={styles.sortable}>Status {sortKey === 'transaction_status' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.length > 0 ? (
                                    transactions.map((row, idx) => (
                                        <tr key={row.transaction_id ?? idx}>
                                            <td>{row.transaction_id}</td>
                                            <td>{row.user_name || 'N/A'}</td>
                                            <td>{row.service_provider || 'Razorpay'}</td>
                                            <td className={styles.txId}>{row.service_provider_tid || ''}</td>
                                            <td>₹{Number(row.amount || 0).toFixed(2)}</td>
                                            <td>₹{Number(row.net_amount || row.amount || 0).toFixed(2)}</td>
                                            <td>{formatAdminDate(row.transaction_date, 'N/A')}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${getStatusClass(row.transaction_status)}`}>
                                                    {(row.transaction_status || 'UNKNOWN').toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    {hasPermission("MODIFY_PAYMENT_GATEWAY") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.edit}`}
                                                            title="Edit"
                                                            onClick={() => {
                                                                setSelectedGatewayId(row.transaction_id);
                                                                setIsModalOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    )}
                                                    {hasPermission("DELETE_PAYMENT_GATEWAY") && (
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.delete}`}
                                                            title="Delete"
                                                            onClick={() => handleDelete(row.transaction_id)}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={9} className={styles.emptyState}>
                                            No payment gateway logs found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {!loading && transactions.length > 0 && (
                    <div className={styles.pagination}>
                        <div className={styles.paginationInfo}>
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, transactions.length * totalPages)} of {transactions.length * totalPages} entries
                        </div>
                        <div className={styles.paginationControls}>
                            <button
                                className={styles.pageBtn}
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                &lt;
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                                if (
                                    p === 1 ||
                                    p === totalPages ||
                                    (p >= currentPage - 1 && p <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={p}
                                            className={`${styles.pageBtn} ${currentPage === p ? styles.active : ""}`}
                                            onClick={() => setCurrentPage(p)}
                                        >
                                            {p}
                                        </button>
                                    );
                                }
                                if (p === currentPage - 2 || p === currentPage + 2) {
                                    return <span key={p} className={styles.paginationEllipsis}>...</span>;
                                }
                                return null;
                            })}

                            <button
                                className={styles.pageBtn}
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                &gt;
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedGatewayId ? "Edit Payment Gateway Record" : "Add Payment Gateway Record"}
                    maxWidth="1200px"
                    disableBackdropClick={true}
                >
                    <AddPaymentGateway
                        gatewayId={selectedGatewayId}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchTransactions(currentPage);
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    );
};

export default PaymentGatewayList;
